import {
  AccountBalance as AccountBalanceIcon,
  Business as BusinessIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModelType, RecentlyViewedItem } from 'RecentlyViewed';
import { Palette } from 'Palette';
import StyledSection from '../../components/styles/StyledSection';
import { StyledHeading } from '../../components/styles/styles';
import useRecentlyViewed from '../../hooks/admin/useRecentlyViewed';

interface RecentlyViewedPageProps {
  tone?: keyof typeof Palette;
}

/**
 * Full-page component for displaying recently viewed items with advanced filtering and pagination
 */
export const RecentlyViewedPage: React.FC<RecentlyViewedPageProps> = ({ tone = 'tone1' }) => {
  const [items, setItems] = useState<RecentlyViewedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [selectedModelType, setSelectedModelType] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const { getRecentlyViewed } = useRecentlyViewed();
  const navigate = useNavigate();

  const loadRecentlyViewed = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getRecentlyViewed(
        selectedModelType || undefined,
        itemsPerPage,
        currentPage,
      );
      setItems(response.data);
      setPagination(response.pagination);
    } catch (err) {
      console.warn('Failed to load recently viewed items:', err);
    } finally {
      setLoading(false);
    }
  }, [getRecentlyViewed, currentPage, selectedModelType, itemsPerPage]);

  useEffect(() => {
    void loadRecentlyViewed();
  }, [loadRecentlyViewed]);

  const getNavigationPath = (type: string, id: string) => {
    const modelTypeToPath: Record<string, string> = {
      CONTACT: `/contacts/${id}`,
      ADMIN_USER: `/adminUsers/${id}`,
      INVESTOR: `/investors/${id}`,
      FUND_OPPORTUNITY: `/opportunities/${id}`,
      INVESTMENT_ENTITY: `/entities/${id}`,
      EOI: `/opportunities/${id}`,
      ALLOCATION: `/allocations/${id}`,
      GLOBAL_CONFIG: `/globalConfig`,
      USER: `/users/${id}`,
    };
    return modelTypeToPath[type] || `/models/${type.toLowerCase()}/${id}`;
  };

  const handleItemClick = (item: RecentlyViewedItem) => {
    const path = getNavigationPath(item.modelType, item.modelId);
    navigate(path);
  };

  const getModelIcon = (modelType: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      CONTACT: <PersonIcon sx={{ fontSize: 20 }} />,
      FUND_OPPORTUNITY: <BusinessIcon sx={{ fontSize: 20 }} />,
      INVESTMENT_ENTITY: <AccountBalanceIcon sx={{ fontSize: 20 }} />,
      INVESTOR: <WorkIcon sx={{ fontSize: 20 }} />,
      ADMIN_USER: <PersonIcon sx={{ fontSize: 20 }} />,
    };
    return iconMap[modelType] || <BusinessIcon sx={{ fontSize: 20 }} />;
  };

  const getModelDisplayName = (item: RecentlyViewedItem) => {
    if (item.modelData) {
      if (item.modelData.firstName && item.modelData.lastName) {
        return `${item.modelData.firstName} ${item.modelData.lastName}`;
      }
      if (item.modelData.name) {
        return item.modelData.name;
      }
      if (item.modelData.email) {
        return item.modelData.email;
      }
    }
    return item.modelId;
  };

  const getModelSubtitle = (item: RecentlyViewedItem) => {
    if (item.modelData) {
      if (item.modelData.email && item.modelData.firstName) {
        return item.modelData.email;
      }
      if (item.modelData.summaryTitle) {
        return item.modelData.summaryTitle;
      }
      if (item.modelData.type) {
        return item.modelData.type;
      }
    }
    return item.modelType;
  };

  const getModelTypeColor = (modelType: string) => {
    const colorMap: Record<string, string> = {
      CONTACT: '#2196F3',
      FUND_OPPORTUNITY: '#4CAF50',
      INVESTMENT_ENTITY: '#FF9800',
      INVESTOR: '#9C27B0',
      ADMIN_USER: '#607D8B',
    };
    return colorMap[modelType] || '#757575';
  };

  const filteredItems = items.filter((item) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const displayName = getModelDisplayName(item).toLowerCase();
      const subtitle = getModelSubtitle(item).toLowerCase();
      const modelType = item.modelType.toLowerCase();

      return (
        displayName.includes(searchLower) ||
        subtitle.includes(searchLower) ||
        modelType.includes(searchLower)
      );
    }
    return true;
  });

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleModelTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedModelType(event.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <StyledSection tone={tone}>
      <StyledHeading tone={tone}>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          Recently Viewed Items
        </Box>
      </StyledHeading>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: Palette[tone].background }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Model Type"
              value={selectedModelType}
              onChange={handleModelTypeChange}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              {Object.values(ModelType).map((type) => (
                <MenuItem key={type} value={type}>
                  {type.replace('_', ' ')}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Items per Page"
              select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              fullWidth
              size="small"
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              placeholder="Search by name, email, or type..."
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Results Count */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredItems.length} of {pagination?.total || 0} recently viewed items
        </Typography>
      </Box>

      {/* Items Grid */}
      <Grid container spacing={2}>
        {filteredItems.map((item, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            key={`${item.modelType}-${item.modelId}-${index}`}
          >
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                bgcolor: Palette[tone].background,
                border: `1px solid ${Palette[tone].border}`,
              }}
              onClick={() => handleItemClick(item)}
            >
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      mr: 1,
                      bgcolor: getModelTypeColor(item.modelType),
                    }}
                  >
                    {getModelIcon(item.modelType)}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                      {getModelDisplayName(item)}
                    </Typography>
                    <Chip
                      label={item.modelType.replace('_', ' ')}
                      size="small"
                      sx={{
                        fontSize: '0.6rem',
                        height: 20,
                        bgcolor: getModelTypeColor(item.modelType),
                        color: 'white',
                      }}
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {getModelSubtitle(item)}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Typography variant="caption" color="text.secondary">
                  Viewed {format(new Date(item.viewedAt), 'MMM d, yyyy h:mm a')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* No Results */}
      {filteredItems.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No recently viewed items found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search terms
          </Typography>
        </Box>
      )}
    </StyledSection>
  );
};

export default RecentlyViewedPage;
